import { z } from "zod";
import { ABSENCE_KINDS, ABSENCE_STATUSES, LEAVE_KINDS } from "../enums.js";
import { idSchema, isoDateSchema, isoTimestampSchema } from "./common.js";

export const REASON_MAX_LENGTH = 200;
export const DECISION_NOTE_MAX_LENGTH = 200;

export const absenceDocumentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  uploadedAt: isoTimestampSchema,
});
export type AbsenceDocument = z.infer<typeof absenceDocumentSchema>;

export const absenceRequestSchema = z.object({
  id: z.string(),
  zvEmployeeId: z.number().int(),
  kind: z.enum(ABSENCE_KINDS),
  absenceTypeKey: z.string(),
  dateFrom: isoDateSchema,
  dateTo: isoDateSchema.nullable(),
  wholeDay: z.boolean(),
  reason: z.string(),
  status: z.enum(ABSENCE_STATUSES),
  workdays: z.number().int(),
  decidedBy: z.string().nullable(),
  decidedAt: isoTimestampSchema.nullable(),
  decisionNote: z.string().nullable(),
  acknowledgedBy: z.string().nullable(),
  acknowledgedAt: isoTimestampSchema.nullable(),
  syncError: z.string().nullable(),
  // A failed outbound job carries this request: "Erneut senden" re-queues it (B1).
  retryable: z.boolean(),
  withdrawRequestedAt: isoTimestampSchema.nullable(),
  withdrawnAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  documents: z.array(absenceDocumentSchema),
  // Whether the owner may still withdraw (server-side rule, mirrored by the UI).
  canWithdraw: z.boolean(),
  // AZK requests: hours taken off the working-time account (information only); null otherwise
  // or when no assignment covers the workdays.
  reductionHours: z.number().nullable(),
});
export type AbsenceRequest = z.infer<typeof absenceRequestSchema>;

// Vacation balance of one year as mirrored from zvoove plus what this app has planned on top.
export const vacationBalanceSchema = z.object({
  year: z.number().int(),
  eligibleDays: z.number(),
  carriedOver: z.number(),
  consumed: z.number(),
  remaining: z.number(),
  totalAvailable: z.number(),
  // Workdays of submitted/approved vacation requests of that year not yet in zvoove.
  planned: z.number(),
  syncedAt: isoTimestampSchema,
});
export type VacationBalance = z.infer<typeof vacationBalanceSchema>;

// AZK balance (working-time account) of one mirrored month, extension 2. Since extension 3 the
// balance shown is the previous calendar month's (AZK is closed monthly).
export const overtimeBalanceSchema = z.object({
  // `YYYY-MM`
  month: z.string(),
  hours: z.number(),
  syncedAt: isoTimestampSchema,
});
export type OvertimeBalance = z.infer<typeof overtimeBalanceSchema>;

export const absencesResponseSchema = z.object({
  balance: vacationBalanceSchema.nullable(),
  overtime: overtimeBalanceSchema.nullable(),
  // `YYYY-MM` of the month `overtime` stands for (the previous Berlin month), also when it is
  // missing: the card then says "Noch kein AZK-Stand für <Monat>" (extension 3).
  overtimeMonth: z.string(),
  requests: z.array(absenceRequestSchema),
});
export type AbsencesResponse = z.infer<typeof absencesResponseSchema>;

export const vacationPreviewBodySchema = z.object({
  dateFrom: isoDateSchema,
  dateTo: isoDateSchema,
});
export type VacationPreviewBody = z.infer<typeof vacationPreviewBodySchema>;

// `clientId` makes a retried POST idempotent (offline queue): a repeat returns the existing request.
// The reason is optional (extension 2); the outbound job sends a fallback text to zvoove.
export const vacationRequestBodySchema = vacationPreviewBodySchema.extend({
  reason: z.string().trim().max(REASON_MAX_LENGTH).optional(),
  clientId: z.uuid().optional(),
});
export type VacationRequestBody = z.infer<typeof vacationRequestBodySchema>;

// Vacation and AZK requests share body, preview and rules (`POST /absences/azk` takes the same).
export const leaveKindSchema = z.enum(LEAVE_KINDS);

export const absenceConflictSchema = z.object({
  id: z.string(),
  kind: z.enum(ABSENCE_KINDS),
  status: z.enum(ABSENCE_STATUSES),
  dateFrom: isoDateSchema,
  dateTo: isoDateSchema.nullable(),
});
export type AbsenceConflict = z.infer<typeof absenceConflictSchema>;

export const vacationPreviewResponseSchema = z.object({
  workdays: z.number().int(),
  remaining: z.number().nullable(),
  planned: z.number(),
  remainingAfter: z.number().nullable(),
  holidays: z.array(z.object({ date: isoDateSchema, name: z.string() })),
  conflicts: z.array(absenceConflictSchema),
  // Dates of the range zvoove already has an absence on (mirror), if known.
  blockedDates: z.array(isoDateSchema),
  // The field errors the request would fail with (same codes as the POST), empty when valid.
  fieldErrors: z.record(z.string(), z.string()),
  // AZK preview: hours the request takes off the account and the current AZK balance.
  reductionHours: z.number().nullable(),
  overtime: overtimeBalanceSchema.nullable(),
  overtimeMonth: z.string(),
  // Hours of the employee's other AZK requests not in zvoove yet, and the AZK balance after
  // those and this request (null without balance or hours). See `azkOutlook` in the api.
  overtimePlanned: z.number().nullable(),
  overtimeAfter: z.number().nullable(),
});
export type VacationPreviewResponse = z.infer<typeof vacationPreviewResponseSchema>;

// Multipart fields of `POST /absences/sick` (the document is the file part).
export const sickReportFieldsSchema = z.object({
  dateFrom: isoDateSchema,
  dateTo: isoDateSchema.optional(),
  note: z.string().trim().max(REASON_MAX_LENGTH).optional(),
});
export type SickReportFields = z.infer<typeof sickReportFieldsSchema>;

export const absenceIdParamsSchema = z.object({ id: idSchema });
export const documentIdParamsSchema = z.object({ documentId: idSchema });

export const absenceResponseSchema = z.object({ request: absenceRequestSchema });
export type AbsenceResponse = z.infer<typeof absenceResponseSchema>;

// Dispatcher side -------------------------------------------------------------------------------

export const dispatchAbsencesQuerySchema = z.object({
  status: z.enum(ABSENCE_STATUSES).optional(),
  kind: z.enum(ABSENCE_KINDS).optional(),
  employeeId: z.coerce.number().int().positive().optional(),
  // "needs action": submitted vacation/AZK or unacknowledged sick reports (the latter only while
  // sick reports are switched on).
  open: z.enum(["true"]).optional(),
});
export type DispatchAbsencesQuery = z.infer<typeof dispatchAbsencesQuerySchema>;

export const dispatchAbsenceRowSchema = absenceRequestSchema.extend({
  employee: z.object({
    zvEmployeeId: z.number().int(),
    firstName: z.string(),
    lastName: z.string(),
    personnelNo: z.number().int().nullable(),
  }),
  // Snapshot of the employee's balance for the request's year; null when not mirrored yet.
  balance: z
    .object({
      year: z.number().int(),
      remaining: z.number(),
      planned: z.number(),
      // remaining − planned (other requests) − this request's workdays; only for vacation.
      remainingAfter: z.number().nullable(),
    })
    .nullable(),
  // AZK balance of the employee (previous month, extension 3); only filled for AZK requests.
  overtime: overtimeBalanceSchema.nullable(),
  // The month `overtime` stands for, and the running month's value as provisional (AZK only).
  overtimeMonth: z.string().nullable(),
  overtimeCurrent: overtimeBalanceSchema.nullable(),
  // AZK requests: hours of the employee's other unsynced AZK requests and the balance after
  // them and this one — only while this request is submitted/approved, else null.
  overtimePlanned: z.number().nullable(),
  overtimeAfter: z.number().nullable(),
});
export type DispatchAbsenceRow = z.infer<typeof dispatchAbsenceRowSchema>;

export const dispatchAbsencesResponseSchema = z.object({
  absences: z.array(dispatchAbsenceRowSchema),
});
export type DispatchAbsencesResponse = z.infer<typeof dispatchAbsencesResponseSchema>;

export const dispatchAbsenceResponseSchema = z.object({ absence: dispatchAbsenceRowSchema });
export type DispatchAbsenceResponse = z.infer<typeof dispatchAbsenceResponseSchema>;

export const rejectAbsenceBodySchema = z.object({
  note: z.string().trim().min(1).max(DECISION_NOTE_MAX_LENGTH),
});
export type RejectAbsenceBody = z.infer<typeof rejectAbsenceBodySchema>;

// Absence part of `GET /dispatch/inbox` (the time part belongs to the time-entries area).
// `openAbsences` counts vacation and AZK requests; the sick part is 0/empty while sick reports
// are switched off (extension 2).
export const inboxAbsencesSchema = z.object({
  openAbsences: z.number().int(),
  newSickReports: z.number().int(),
  openAbsenceItems: z.array(dispatchAbsenceRowSchema),
  newSickReportItems: z.array(dispatchAbsenceRowSchema),
});
export type InboxAbsences = z.infer<typeof inboxAbsencesSchema>;
